import { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import PageHeader from '../../components/common/PageHeader';
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '../../components/ui/accordion';
import {
  Search,
  MessageCircle,
  BookOpen,
  HelpCircle,
  Mail,
  TrendingUp,
  DollarSign,
  Settings,
} from 'lucide-react';

interface FAQItem {
  id: string;
  category: string;
  question: string;
  answer: string;
  helpful: number;
  notHelpful: number;
}

const FAQ = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const categories = [
    { id: 'all', name: 'All Categories', icon: HelpCircle },
    { id: 'account', name: 'Account', icon: BookOpen },
    { id: 'trading', name: 'Trading', icon: TrendingUp },
    { id: 'deposit', name: 'Deposits & Withdrawals', icon: DollarSign },
    { id: 'technical', name: 'Technical Issues', icon: Settings },
  ];

  const faqData: FAQItem[] = [
    {
      id: '1',
      category: 'account',
      question: 'How do I open a live trading account?',
      answer:
        'To open a live trading account, click on "Open Live Account" from the dashboard, complete the registration form, verify your identity with KYC documents, and make your initial deposit. The process typically takes 1-2 business days.',
      helpful: 45,
      notHelpful: 3,
    },
    {
      id: '2',
      category: 'trading',
      question: 'What is the minimum deposit amount?',
      answer:
        'The minimum deposit amount varies by account type: Standard accounts require $100, Premium accounts require $1,000, and VIP accounts require $10,000. All deposits are processed instantly via most payment methods.',
      helpful: 38,
      notHelpful: 2,
    },
    {
      id: '3',
      category: 'trading',
      question: 'What leverage options are available?',
      answer:
        'We offer flexible leverage options up to 1:500 for professional clients and 1:30 for retail clients. Leverage can be adjusted based on your trading experience and risk tolerance.',
      helpful: 52,
      notHelpful: 5,
    },
    {
      id: '4',
      category: 'deposit',
      question: 'How long do withdrawals take to process?',
      answer:
        'Withdrawal processing times vary by method: Bank transfers take 2-5 business days, credit/debit cards take 3-7 business days, and e-wallets are processed within 24 hours. All withdrawals are subject to security verification.',
      helpful: 41,
      notHelpful: 8,
    },
    {
      id: '5',
      category: 'account',
      question: 'How do I reset my password?',
      answer:
        'Click on "Forgot Password" on the login page, enter your registered email address, and follow the link sent to your email. You can also contact support for immediate assistance.',
      helpful: 29,
      notHelpful: 1,
    },
    {
      id: '6',
      category: 'technical',
      question: "Why can't I access my trading platform?",
      answer:
        'Platform access issues can be caused by incorrect credentials, server maintenance, or network problems. First, verify your login details, check our server status page, and ensure your internet connection is stable. If issues persist, contact our technical support team.',
      helpful: 33,
      notHelpful: 4,
    },
  ];

  const filteredFAQs = faqData.filter(item => {
    const matchesSearch =
      item.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.answer.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory =
      selectedCategory === 'all' || item.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="space-y-6">
      <div className="sticky top-0 z-20 bg-background pt-4">
        <PageHeader
          title="Frequently Asked Questions"
          description="Find answers to common questions about our trading platform"
        />
        {/*Search input and tab buttons*/}
        {/* Mobile View */}
        <div className="md:hidden pt-4 flex flex-col gap-4">
          <div className="relative flex-1 w-full">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              placeholder="Search FAQs..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="pl-10 w-full"
            />
          </div>
          <div className="overflow-x-auto pb-2">
            <div className="flex gap-2 min-w-0">
              {categories.map(category => {
                const Icon = category.icon;
                return (
                  <Button
                    key={category.id}
                    variant={
                      selectedCategory === category.id ? 'default' : 'outline'
                    }
                    size="sm"
                    onClick={() => setSelectedCategory(category.id)}
                    className="flex items-center gap-2 whitespace-nowrap flex-shrink-0"
                  >
                    <Icon className="h-4 w-4 flex-shrink-0" />
                    {category.name}
                  </Button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Desktop View */}
        <div className="hidden md:flex pt-4 flex-row gap-4 items-start justify-between">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              placeholder="Search FAQs..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="pl-10 w-full"
            />
          </div>
          <div className="overflow-x-auto pb-2">
            <div className="flex gap-2 min-w-0 flex-nowrap">
              {categories.map(category => {
                const Icon = category.icon;
                return (
                  <Button
                    key={category.id}
                    variant={
                      selectedCategory === category.id ? 'default' : 'outline'
                    }
                    size="sm"
                    onClick={() => setSelectedCategory(category.id)}
                    className="flex items-center gap-2 whitespace-nowrap flex-shrink-0"
                  >
                    <Icon className="h-4 w-4 flex-shrink-0" />
                    {category.name}
                  </Button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          {filteredFAQs.length === 0 ? (
            <Card>
              <CardContent className="text-center py-8">
                <HelpCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No FAQs Found
                </h3>
                <p className="text-gray-500">
                  Try adjusting your search or browse different categories
                </p>
              </CardContent>
            </Card>
          ) : (
            <Accordion type="single" className="space-y-4" collapsible>
              {filteredFAQs.map(item => (
                <AccordionItem
                  key={item.id}
                  value={item.id}
                  className="border rounded-lg px-4"
                >
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-2 text-left">
                      <Badge variant="secondary" className="text-xs">
                        {categories.find(c => c.id === item.category)?.name}
                      </Badge>
                      <span className="text-lg font-medium leading-6">
                        {item.question}
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <p className="text-gray-600 mb-4">{item.answer}</p>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span>Was this helpful?</span>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" className="text-xs">
                          👍 {item.helpful}
                        </Button>
                        <Button variant="outline" size="sm" className="text-xs">
                          👎 {item.notHelpful}
                        </Button>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageCircle className="h-5 w-5" />
                Need More Help?
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-gray-600">
                Can&apos;t find what you&apos;re looking for? Our support team
                is here to help.
              </p>
              <Button className="w-full flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Contact Support
              </Button>
              <div className="text-sm text-gray-500">
                <p>Response time: Usually within 2 hours</p>
                <p>Available: 24/7</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Quick Links</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <a
                href="#"
                className="block text-blue-600 hover:text-blue-800 hover:underline"
              >
                Trading Guide for Beginners
              </a>
              <a
                href="#"
                className="block text-blue-600 hover:text-blue-800 hover:underline"
              >
                Platform Tutorials
              </a>
              <a
                href="#"
                className="block text-blue-600 hover:text-blue-800 hover:underline"
              >
                Risk Management
              </a>
              <a
                href="#"
                className="block text-blue-600 hover:text-blue-800 hover:underline"
              >
                Account Verification
              </a>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Popular Topics</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                <Badge variant="outline">Account Setup</Badge>
                <Badge variant="outline">Deposits</Badge>
                <Badge variant="outline">Leverage</Badge>
                <Badge variant="outline">Security</Badge>
                <Badge variant="outline">Fees</Badge>
                <Badge variant="outline">Mobile App</Badge>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default FAQ;
